/**
 * Single Evidence Detail Full Page
 * Route: /evidence/:id
 * 
 * Full-page view for deep evidence review with all tabs:
 * - Overview: File preview, metadata, classification
 * - Usage (Superpower #1): Cross-framework intelligence
 * - Audit Trail (Superpower #3): Immutable event log
 * - Edit: Metadata editing
 * - History: Version history
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Share2,
  Edit,
  Trash2,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  FileSpreadsheet,
  FileImage,
  File,
  Calendar,
  Tag,
  User,
  Network,
  Shield,
  ChevronRight,
  Loader2,
  AlertTriangle,
  History,
  Link2,
  Eye,
  Copy
} from 'lucide-react';
import { API } from '../../App';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { useToast } from '../../hooks/use-toast';
import { apiFetch } from '../../services/api_service';


// ============================================================================
// HELPERS
// ============================================================================

const getFileIcon = (filename) => {
  if (!filename) return File;
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf': return FileText;
    case 'xlsx':
    case 'xls':
    case 'csv': return FileSpreadsheet;
    case 'png':
    case 'jpg':
    case 'jpeg': return FileImage;
    default: return File;
  }
};

const getApprovalStatusConfig = (status) => {
  switch (status) {
    case 'approved':
      return { label: 'Approved', bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 };
    case 'rejected':
      return { label: 'Rejected', bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle };
    case 'pending':
    default:
      return { label: 'Pending Approval', bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Clock };
  }
};

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function SingleEvidenceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [evidence, setEvidence] = useState(null);
  const [usageMap, setUsageMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isApproving, setIsApproving] = useState(false);

  // Fetch evidence details
  const fetchEvidence = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [evidenceRes, usageRes] = await Promise.all([
        fetch(`${API}/v1/evidence/${id}`, { headers }),
        fetch(`${API}/v1/evidence/${id}/detailed-usage-map`, { headers })
      ]);
      
      if (evidenceRes) {
        setEvidence(await evidenceRes.json());
      } else {
        throw new Error('Evidence not found');
      }
      
      if (usageRes) {
        setUsageMap(await usageRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch evidence:', error);
      toast({
        title: 'Error',
        description: 'Failed to load evidence details',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    fetchEvidence();
  }, [fetchEvidence]);

  // Action handlers
  const handleApprove = async () => {
    setIsApproving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API}/v1/evidence/${id}/approval?approval_status=approved`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        toast({ title: 'Evidence approved', description: 'The evidence has been approved.' });
        fetchEvidence();
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: 'Error', description: err.detail || 'Failed to approve evidence', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to approve evidence', variant: 'destructive' });
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;
    
    setIsApproving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API}/v1/evidence/${id}/approval?approval_status=rejected&notes=${encodeURIComponent(reason)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        toast({ title: 'Evidence rejected', description: 'The owner has been notified.', variant: 'destructive' });
        fetchEvidence();
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: 'Error', description: err.detail || 'Failed to reject evidence', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reject evidence', variant: 'destructive' });
    } finally {
      setIsApproving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const data = await apiFetch(`/v1/evidence/${id}/download`);
      if (data && !data._error && data.download_type === 'redirect' && data.url) {
        window.open(data.url, '_blank');
      } else if (data?._error) {
        throw new Error(data.message);
      }
    } catch (error) {
      toast({ title: 'Download failed', description: error.message, variant: 'destructive' });
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast({ title: 'Link copied', description: 'Share this link with your team.' });
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this evidence?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/v1/evidence/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast({ title: 'Evidence deleted', description: 'The evidence has been removed.' });
        navigate('/evidence');
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: 'Delete failed', description: err.detail || 'Failed to delete evidence', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Delete failed', description: error.message, variant: 'destructive' });
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-emerald-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading evidence details...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!evidence) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Evidence not found</h2>
          <p className="text-gray-400 mb-4">This evidence may have been deleted or you don't have access.</p>
          <Button onClick={() => navigate('/evidence')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Evidence Hub
          </Button>
        </div>
      </div>
    );
  }

  const FileIcon = getFileIcon(evidence.original_filename);
  const approvalConfig = evidence.ownership ? getApprovalStatusConfig(evidence.ownership.approval_status) : getApprovalStatusConfig('pending');
  const ApprovalIcon = approvalConfig.icon;

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="evidence-detail-page">
      {/* Breadcrumb Header */}
      <div className="border-b border-[#1F1F1F] bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link 
            to="/evidence" 
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-emerald-400 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Evidence Hub
          </Link>
        </div>
      </div>

      {/* Main Header */}
      <div className="border-b border-[#1F1F1F]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between gap-6">
            {/* File Info */}
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="h-16 w-16 rounded-xl bg-[#1F1F1F] flex items-center justify-center shrink-0">
                <FileIcon className="h-8 w-8 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-white truncate">
                  {evidence.original_filename}
                </h1>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {new Date(evidence.uploaded_at).toLocaleDateString()}
                  </span>
                  <span>
                    {evidence.file_size ? `${Math.round(evidence.file_size / 1024)} KB` : 'Size unknown'}
                  </span>
                  {evidence.category && (
                    <Badge variant="outline" className="text-gray-300 border-gray-600">
                      {evidence.category}
                    </Badge>
                  )}
                </div>
                
                {/* Tags */}
                {evidence.tags && evidence.tags.length > 0 && (
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    {evidence.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="bg-[#1F1F1F] text-gray-300 text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Status & Quick Actions */}
            <div className="flex items-center gap-3 shrink-0">
              <Badge className={`${approvalConfig.bg} ${approvalConfig.text} border-0 px-3 py-1`}>
                <ApprovalIcon className="h-4 w-4 mr-1.5" />
                {approvalConfig.label}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="border-b border-[#1F1F1F] bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {evidence.ownership?.approval_status === 'pending' && (
                <>
                  <Button 
                    size="sm" 
                    onClick={handleApprove}
                    disabled={isApproving}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1.5" />
                    Approve
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={handleReject}
                    disabled={isApproving}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                  >
                    <XCircle className="h-4 w-4 mr-1.5" />
                    Reject
                  </Button>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={handleDownload} className="border-[#2A2A2A] text-gray-300">
                <Download className="h-4 w-4 mr-1.5" />
                Download
              </Button>
              <Button size="sm" variant="outline" onClick={handleShare} className="border-[#2A2A2A] text-gray-300">
                <Copy className="h-4 w-4 mr-1.5" />
                Copy Link
              </Button>
              <Button size="sm" variant="outline" onClick={() => setActiveTab('edit')} className="border-[#2A2A2A] text-gray-300">
                <Edit className="h-4 w-4 mr-1.5" />
                Edit
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleDelete}
                className="border-red-500/30 text-red-400 hover:bg-red-500/20"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with Tabs */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#1A1A1A] border border-[#2A2A2A]">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="usage">
              Usage
              {usageMap && usageMap.totalFrameworks > 0 && (
                <Badge variant="secondary" className="ml-2 bg-teal-500/20 text-teal-400">
                  {usageMap.totalFrameworks}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="audit">Audit Trail</TabsTrigger>
            <TabsTrigger value="edit">Edit</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 m-0">
            <OverviewTab evidence={evidence} FileIcon={FileIcon} />
          </TabsContent>

          {/* Usage Tab */}
          <TabsContent value="usage" className="space-y-6 m-0">
            <UsageTab usageMap={usageMap} evidenceId={id} />
          </TabsContent>

          {/* Audit Trail Tab */}
          <TabsContent value="audit" className="space-y-6 m-0">
            <AuditTrailTab evidence={evidence} />
          </TabsContent>

          {/* Edit Tab */}
          <TabsContent value="edit" className="space-y-6 m-0">
            <EditTab evidence={evidence} onSuccess={fetchEvidence} onCancel={() => setActiveTab('overview')} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ============================================================================
// OVERVIEW TAB
// ============================================================================

function OverviewTab({ evidence, FileIcon }) {
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Left Column - File Preview & Description */}
      <div className="lg:col-span-2 space-y-6">
        {/* File Preview */}
        <Card className="bg-[#111111] border-[#1F1F1F]">
          <CardHeader>
            <CardTitle className="text-base text-white">File Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] flex flex-col items-center justify-center">
              <FileIcon className="h-20 w-20 text-gray-600 mb-4" />
              <p className="text-sm text-gray-300">{evidence.original_filename}</p>
              <p className="text-xs text-gray-500 mt-1">
                {evidence.file_size ? `${Math.round(evidence.file_size / 1024)} KB` : 'Unknown size'}
              </p>
              <Button variant="outline" size="sm" className="mt-4 border-[#2A2A2A] text-gray-300">
                <Eye className="h-4 w-4 mr-2" />
                Preview File
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Description */}
        <Card className="bg-[#111111] border-[#1F1F1F]">
          <CardHeader>
            <CardTitle className="text-base text-white flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Description
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-300 leading-relaxed">
              {evidence.description || 'No description provided.'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Right Column - Metadata */}
      <div className="space-y-6">
        {/* Classification */}
        <Card className="bg-[#111111] border-[#1F1F1F]">
          <CardHeader>
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Classification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Evidence Type</p>
              <Badge variant="outline" className="text-gray-300 border-gray-600">
                {evidence.evidence_type || 'Not specified'}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Category</p>
              <Badge variant="outline" className="text-gray-300 border-gray-600">
                {evidence.category || 'Not specified'}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <Badge variant="outline" className="text-gray-300 border-gray-600 capitalize">
                {evidence.status || 'uploaded'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Upload Info */}
        <Card className="bg-[#111111] border-[#1F1F1F]">
          <CardHeader>
            <CardTitle className="text-base text-white flex items-center gap-2">
              <User className="h-4 w-4" />
              Upload Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded By</p>
              <p className="text-sm text-gray-300">{evidence.uploaded_by_user_id || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Upload Date</p>
              <p className="text-sm text-gray-300">{new Date(evidence.uploaded_at).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Storage Type</p>
              <p className="text-sm text-gray-300">{evidence.storage_type || 'GridFS'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Technical Details */}
        <Card className="bg-[#111111] border-[#1F1F1F]">
          <CardHeader>
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Technical Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {evidence.file_hash && (
              <div>
                <p className="text-xs text-gray-500 mb-1">File Hash (SHA-256)</p>
                <p className="text-xs font-mono text-gray-400 break-all">{evidence.file_hash}</p>
              </div>
            )}
            {evidence.id && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Evidence ID</p>
                <p className="text-xs font-mono text-gray-400">{evidence.id}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================================================
// USAGE TAB (Superpower #1)
// ============================================================================

function UsageTab({ usageMap, evidenceId }) {
  if (!usageMap || usageMap.totalFrameworks === 0) {
    return (
      <Card className="bg-[#111111] border-[#1F1F1F]">
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <Network className="h-16 w-16 text-gray-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Not mapped to any frameworks</h3>
          <p className="text-sm text-gray-400 mb-6 max-w-md">
            Map this evidence to framework requirements to use it in your disclosures and maximize reuse across multiple frameworks.
          </p>
          <Link to={`/evidence?selected=${evidenceId}`}>
            <Button variant="outline" className="border-emerald-500/30 text-emerald-400">
              <Link2 className="h-4 w-4 mr-2" />
              Add Framework Mappings
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card className="bg-teal-500/10 border-teal-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-teal-300">
            <Network className="h-5 w-5" />
            Cross-Framework Intelligence (Superpower #1)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center p-4 rounded-lg bg-[#1A1A1A] border border-teal-500/30">
              <p className="text-3xl font-bold text-teal-400">{usageMap.totalFrameworks}</p>
              <p className="text-xs text-gray-400">Frameworks</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-[#1A1A1A] border border-teal-500/30">
              <p className="text-3xl font-bold text-teal-400">{usageMap.totalRequirements}</p>
              <p className="text-xs text-gray-400">Requirements</p>
            </div>
          </div>
          <p className="text-sm text-teal-300">
            This evidence satisfies <strong>{usageMap.totalRequirements}</strong> requirement{usageMap.totalRequirements !== 1 ? 's' : ''} across <strong>{usageMap.totalFrameworks}</strong> framework{usageMap.totalFrameworks !== 1 ? 's' : ''} - that's a {usageMap.totalFrameworks}x efficiency gain!
          </p>
        </CardContent>
      </Card>

      {/* Framework Mappings */}
      {usageMap.frameworks?.map(framework => (
        <Card key={framework.frameworkCode} className="bg-[#111111] border-[#1F1F1F]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2 text-white">
                <Badge variant="outline" className="font-mono text-gray-300 border-gray-600">
                  {framework.frameworkCode}
                </Badge>
                {framework.frameworkName}
              </CardTitle>
              <Badge variant="secondary" className="bg-[#1F1F1F] text-gray-300">
                {framework.requirements.length} requirement{framework.requirements.length !== 1 ? 's' : ''}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {framework.requirements.map((req, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 p-3 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] hover:border-teal-500/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {req.requirementCode && (
                      <Badge variant="outline" className="font-mono text-xs text-gray-300 border-gray-600">
                        {req.requirementCode}
                      </Badge>
                    )}
                    {req.mappingType === 'ai_suggested' && (
                      <Badge className="bg-purple-500/20 text-purple-300 border-0 text-xs">
                        AI {Math.round((req.mappingConfidence || 0) * 100)}%
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-300">
                    {req.requirementTitle || 'Mapped requirement'}
                  </p>
                  {req.notes && (
                    <p className="text-xs text-gray-500 mt-1">{req.notes}</p>
                  )}
                </div>
                <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ============================================================================
// AUDIT TRAIL TAB (Superpower #3)
// ============================================================================

function AuditTrailTab({ evidence }) {
  const auditEvents = [
    {
      type: 'uploaded',
      title: 'Evidence Uploaded',
      description: `File uploaded by ${evidence.uploaded_by_user_id || 'system'}`,
      timestamp: evidence.uploaded_at,
      icon: FileText
    }
  ];

  if (evidence.ownership?.approved_at) {
    auditEvents.push({
      type: evidence.ownership.approval_status === 'approved' ? 'approved' : 'rejected',
      title: evidence.ownership.approval_status === 'approved' ? 'Evidence Approved' : 'Evidence Rejected',
      description: `By ${evidence.ownership.approved_by_user_id || 'unknown'}`,
      timestamp: evidence.ownership.approved_at,
      icon: evidence.ownership.approval_status === 'approved' ? CheckCircle2 : XCircle
    });
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 text-blue-300">
              <Shield className="h-5 w-5" />
              Immutable Audit Trail (Superpower #3)
            </CardTitle>
            <Button variant="outline" size="sm" className="border-blue-500/30 text-blue-300">
              <Download className="h-4 w-4 mr-2" />
              Export Audit Pack
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Complete tamper-proof audit trail with cryptographic verification. {auditEvents.length} events recorded.
          </p>
        </CardHeader>
      </Card>

      {/* Timeline */}
      <div className="relative">
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#2A2A2A]" />

        <div className="space-y-6">
          {auditEvents.map((event, idx) => {
            const EventIcon = event.icon;
            const iconBg = event.type === 'approved' ? 'bg-emerald-500/20 text-emerald-400' 
              : event.type === 'rejected' ? 'bg-red-500/20 text-red-400'
              : 'bg-blue-500/20 text-blue-400';

            return (
              <div key={idx} className="relative pl-16">
                <div className={`absolute left-0 top-1 h-12 w-12 rounded-full border-2 border-[#0D0D0D] flex items-center justify-center ${iconBg}`}>
                  <EventIcon className="h-5 w-5" />
                </div>

                <Card className="bg-[#111111] border-[#1F1F1F]">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-semibold text-white">{event.title}</h4>
                          <Badge variant="outline" className="text-xs font-mono text-gray-400 border-gray-600">
                            #{idx + 1}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500">
                          {new Date(event.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <p className="text-sm text-gray-400">{event.description}</p>

                    {evidence.file_hash && idx === 0 && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                        <Shield className="h-3 w-3" />
                        <span className="font-mono">
                          Hash: {evidence.file_hash.substring(0, 16)}...
                        </span>
                        <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-500/30">
                          Verified
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// EDIT TAB
// ============================================================================

function EditTab({ evidence, onSuccess, onCancel }) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    description: evidence.description || '',
    category: evidence.category || '',
    tags: evidence.tags || []
  });
  const [tagInput, setTagInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleAddTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(t => t !== tagToRemove)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const res = await apiFetch(`/v1/evidence/${evidence.id}`, {
        method: 'PATCH',
        body: JSON.stringify(formData)
      });

      if (res && !res._error) {
        toast({ title: 'Evidence updated', description: 'Your changes have been saved.' });
        if (onSuccess) onSuccess();
      } else {
        throw new Error(res?.message || 'Failed to update');
      }
    } catch (error) {
      toast({ title: 'Update failed', description: error.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="bg-[#111111] border-[#1F1F1F]">
      <CardHeader>
        <CardTitle className="text-base text-white">Edit Evidence Metadata</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
          {/* Description */}
          <div className="space-y-2">
            <Label className="text-gray-300">Description</Label>
            <Textarea
              placeholder="Describe this evidence..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="bg-[#1A1A1A] border-[#2A2A2A] text-white resize-none"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label className="text-gray-300">Category</Label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-md text-white"
            >
              <option value="">Select category</option>
              <option value="Emissions">Emissions</option>
              <option value="Energy">Energy</option>
              <option value="Water">Water</option>
              <option value="Biodiversity">Biodiversity</option>
              <option value="Pollution">Pollution</option>
              <option value="Circular Economy">Circular Economy</option>
              <option value="Workforce">Workforce</option>
              <option value="Supply Chain">Supply Chain</option>
              <option value="Governance">Governance</option>
              <option value="Business Conduct">Business Conduct</option>
              <option value="Social">Social</option>
              <option value="Climate">Climate</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label className="text-gray-300">Tags</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Add tags..."
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag(tagInput);
                  }
                }}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => handleAddTag(tagInput)}
                className="border-[#2A2A2A] text-gray-300"
              >
                Add
              </Button>
            </div>

            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="gap-1 bg-[#1F1F1F] text-gray-300">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:text-red-400"
                    >
                      <XCircle className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-4 border-t border-[#1F1F1F]">
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="border-[#2A2A2A] text-gray-300"
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
